import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { corporatePhilanthropyAcceleratorContent } from '../content/educational/corporatePhilanthropyAcceleratorContent';

const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 35;
    const footerHeight = 12;

    // Font sizes
    const fonts = {
      title: 22,
      sectionHeading: 14,
      subHeading: 12,
      body: 11,
      footer: 8
    };

    // Colors
    const colors = {
      blue: [37, 99, 235],
      green: [5, 150, 105],
      gray: [31, 41, 55],
      white: [255, 255, 255]
    };

    // Generate QR Codes
    const qrCodeAcademy = await QRCode.toDataURL('https://causeandimpact.org/academy');
    const qrCodeDonate = await QRCode.toDataURL('https://causeandimpact.org/donate');

    // Add markdown processing
    const processMarkdown = (text) => {
      let segments = [];
      const boldRegex = /\*\*(.*?)\*\*/g;
      const exampleRegex = /\*Example\*\*:/g;
      const caseStudyRegex = /\*Case Study\*\*:/g;
      let lastIndex = 0;
      let match;
      
      // Handle special prefixes
      if (text.startsWith('**Example**:') || 
          text.startsWith('**Case Study**:') ||
          text.startsWith('**Pro Tip**:') ||
          text.startsWith('**Historical Context**:') ||
          text.startsWith("**Today\u2019s CSR Aligns With**:")) {
        const colonIndex = text.indexOf(':');
        return [
          { text: text.substring(2, colonIndex - 2), bold: true },
          { text: ':', bold: false },
          { text: text.substring(colonIndex + 1).trim(), bold: false }
        ];
      }

      // Handle numbered items (e.g., "1) Social Impact:")
      if (/^\d+\)/.test(text.trim())) {
        const parts = text.split(':');
        if (parts.length > 1) {
          return [
            { text: parts[0] + ':', bold: true },
            { text: parts.slice(1).join(':').trim(), bold: false }
          ];
        }
      }
      
      // Process regular bold text
      while ((match = boldRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: text.substring(lastIndex, match.index),
            bold: false
          });
        }
        segments.push({
          text: match[1],
          bold: true
        });
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < text.length) {
        segments.push({
          text: text.substring(lastIndex),
          bold: false
        });
      }
      
      return segments;
    };

    // Header component
    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow effect
      doc.setTextColor(...colors.blue);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(...colors.green);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(...colors.white);
      doc.text('Expert Resources for Nonprofit Success', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeAcademy, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    // Footer component
    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.footer);
      doc.setTextColor(...colors.white);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    };

    // Add Call to Action component
    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const qrSize = 28;
      
      doc.setFillColor(...colors.blue);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 6, 6, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(...colors.white);
      doc.text('Ready to Maximize Your Impact?', pageWidth / 2, yPosition + 18, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Scan to start your corporate giving journey', pageWidth / 2, yPosition + 28, { align: 'center' });
      
      const qrX = pageWidth - qrSize - margin - 6;
      const qrY = yPosition + 8;
      
      // QR code shadow effects
      doc.setFillColor(0, 0, 0, 0.06);
      doc.roundedRect(qrX + 0.8, qrY + 0.8, qrSize, qrSize, 2, 2, 'F');
      doc.setFillColor(0, 0, 0, 0.04);
      doc.roundedRect(qrX + 0.4, qrY + 0.4, qrSize, qrSize, 2, 2, 'F');
      
      // QR code background
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(qrX, qrY, qrSize, qrSize, 2, 2, 'F');
      
      doc.addImage(qrCodeDonate, 'PNG', qrX, qrY, qrSize, qrSize);
    };

    // Process list with proper formatting
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      const bulletIndent = 10;
      const subIndent = 6;
      const lineSpacing = 6;

      // Add page break handler
      const checkPageBreak = (height = 4) => {
        if (currentY + height > pageHeight - footerHeight - 15) {
          doc.addPage();
          addHeader();
          addFooter(doc.internal.getNumberOfPages());
          currentY = headerHeight + 12;
          return true;
        }
        return false;
      };

      lines.forEach(line => {
        const trimmedLine = line.trim();
        
        // Skip empty lines
        if (!trimmedLine) {
          currentY += lineSpacing;
          return;
        }

        // Handle ### headings (like "### Key Components of CSR:")
        if (trimmedLine.startsWith('###')) {
          checkPageBreak(10);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(fonts.subHeading);
          doc.setTextColor(...colors.gray);
          doc.text(trimmedLine.replace('###', '').trim(), margin, currentY);
          currentY += 8;
          return;
        }

        // Handle numbered items with bold (e.g., "1) **Social Impact**:")
        if (/^\d+\)/.test(trimmedLine)) {
          const match = trimmedLine.match(/^(\d+\))\s*\*\*(.*?)\*\*:?(.*)/);
          if (match) {
            checkPageBreak(8);
            const [, number, boldText, restText] = match;
            
            // Number and bold text
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(fonts.body);
            doc.setTextColor(...colors.gray);
            doc.text(`${number} ${boldText}${restText ? ':' : ''}`, margin, currentY);
            
            // Rest of text if any
            if (restText) {
              const textWidth = doc.getTextWidth(`${number} ${boldText}: `);
              doc.setFont('helvetica', 'normal');
              doc.text(restText.trim(), margin + textWidth, currentY);
            }
            
            currentY += 8;
            return;
          }
        }

        // Handle bullet points with proper text wrapping
        if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
          const bulletText = trimmedLine.substring(1).trim();
          const indent = line.startsWith('   ') ? bulletIndent + subIndent : bulletIndent;
          
          checkPageBreak(6);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(fonts.body);
          doc.setTextColor(...colors.gray);
          
          // Add bullet point
          doc.text('•', margin + indent - 4, currentY);
          
          // Calculate available width for wrapped text
          const availableWidth = contentWidth - (margin + indent + 4);
          
          // Handle bold text in bullet points
          const boldMatch = bulletText.match(/\*\*(.*?)\*\*:?(.*)/);
          if (boldMatch) {
            const [, boldText, restText] = boldMatch;
            doc.setFont('helvetica', 'bold');
            
            // Handle bold part
            doc.text(boldText + (restText ? ':' : ''), margin + indent + 4, currentY);
            
            if (restText) {
              const boldWidth = doc.getTextWidth(boldText + ': ');
              doc.setFont('helvetica', 'normal');
              
              // Wrap the rest of the text
              const wrappedText = doc.splitTextToSize(restText.trim(), availableWidth - boldWidth);
              wrappedText.forEach((line, index) => {
                if (index === 0) {
                  doc.text(line, margin + indent + 4 + boldWidth, currentY);
                } else {
                  currentY += lineSpacing;
                  checkPageBreak(lineSpacing);
                  doc.text(line, margin + indent + 4, currentY);
                }
              });
            }
          } else {
            // Handle regular bullet text with wrapping
            const wrappedText = doc.splitTextToSize(bulletText, availableWidth);
            wrappedText.forEach((line, index) => {
              if (index > 0) {
                currentY += lineSpacing;
                checkPageBreak(lineSpacing);
              }
              doc.text(line, margin + indent + 4, currentY);
            });
          }
          
          currentY += lineSpacing;
          return;
        }

        // Handle regular text with proper wrapping
        if (trimmedLine) {
          doc.setFontSize(fonts.body);
          doc.setTextColor(...colors.gray);
          
          // Split text to fit within margins
          const textLines = doc.splitTextToSize(trimmedLine, contentWidth - margin);
          
          textLines.forEach(line => {
            checkPageBreak(lineSpacing);
            renderTextWithBold(line, margin, currentY);
            currentY += lineSpacing;
          });
        } else {
          currentY += lineSpacing;
        }
      });

      return currentY;
    };

    // Add this helper function to process bold text
    const renderTextWithBold = (text, x, y) => {
      const boldPattern = /\*\*(.*?)\*\*/g;
      let lastIndex = 0;
      let currentX = x;
      let match;

      while ((match = boldPattern.exec(text)) !== null) {
        // Render normal text before the bold part
        if (match.index > lastIndex) {
          doc.setFont('helvetica', 'normal');
          const normalText = text.substring(lastIndex, match.index);
          doc.text(normalText, currentX, y);
          currentX += doc.getTextWidth(normalText);
        }

        // Render bold text
        doc.setFont('helvetica', 'bold');
        const boldText = match[1];
        doc.text(boldText, currentX, y);
        currentX += doc.getTextWidth(boldText);

        lastIndex = match.index + match[0].length;
      }

      // Render any remaining normal text
      if (lastIndex < text.length) {
        doc.setFont('helvetica', 'normal');
        doc.text(text.substring(lastIndex), currentX, y);
      }
    };

    // Content generation with enhanced spacing
    const addContent = () => {
      let yPosition = headerHeight + 10;
      let pageNumber = 1;

      // Add title
      doc.setFontSize(fonts.title);
      doc.setTextColor(...colors.blue);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 2, { align: 'center' });
      yPosition += (titleLines.length * 8) + 12;

      // Process sections
      content.forEach((section, index) => {
        if (yPosition + 40 > pageHeight - footerHeight - 15) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 10;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fonts.sectionHeading);
        doc.setTextColor(...colors.green);
        doc.text(section.heading, margin, yPosition);
        yPosition += 10;

        // Section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fonts.body);
        doc.setTextColor(...colors.gray);

        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          if (yPosition + 40 > pageHeight - footerHeight - 15) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 10;
          }

          yPosition = processList(paragraph, yPosition);
          yPosition += 4;
        });

        yPosition += 12;
      });

      // Add CTA
      if (yPosition + 45 > pageHeight - footerHeight - 15) {
        doc.addPage();
        pageNumber++;
        addHeader();
        addFooter(pageNumber);
        yPosition = headerHeight + 10;
      }

      yPosition += 6;
      addCallToAction(yPosition);
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return null;
  }
};

export const generateCorporatePhilanthropyOverview = async () => {
  return generatePDF(
    corporatePhilanthropyAcceleratorContent.sections,
    corporatePhilanthropyAcceleratorContent.title,
    corporatePhilanthropyAcceleratorContent.keywords
  );
};